import React, { Component } from 'react';
import { t, LANGS } from 'i18n';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { Field, Form } from 'react-final-form';
import * as authActions from 'redux/modules/auth';
import loginValidation from './loginValidation';
import registerValidation from './registerValidation';
import forgotValidation from './forgotValidation';
import { newCabinetEnabled, newCabinetUrl, preferClassic, shouldRedirectToNewCabinet } from 'helpers/newCabinet';
import styles from './Login.less';
import LanguagePicker from 'components/LanguagePicker/LanguagePicker';
import { setLang } from 'redux/modules/locale';
import { getSurvey } from 'redux/modules/survey/survey';
import { hasVisibleSurveys, surveysLoaded, POST_REGISTER_KEY } from 'helpers/onboarding';

const TURNSTILE_SITEKEY = '0x4AAAAAAC-3XMGUWG04vtOE';

// Yandex ID and Mail.ru have no id_token flow like Google's, so these are
// plain links, not buttons: the whole page navigates to the provider and comes
// back to /api/v1/login/<id>/callback, which is where the session cookie is
// set. A provider without its REACT_APP_*_ENABLED flag simply isn't rendered.
// The icons are the official brand marks (Yandex red «Я» circle, Mail 2024
// blue «@»), inlined so no external asset load can fail.
const OAUTH_PROVIDERS = [
  {
    id: 'yandex',
    title: 'Яндекс ID',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M2.04 12c0-5.523 4.476-10 10-10 5.522 0 10 4.477 10 10s-4.478 10-10 10c-5.524 0-10-4.477-10-10z" fill="#FC3F1D" />
        <path d="M13.32 7.666h-.924c-1.694 0-2.585.858-2.585 2.123 0 1.43.616 2.1 1.881 2.959l1.045.704-3.003 4.487H7.49l2.695-4.014c-1.55-1.111-2.42-2.19-2.42-4.015 0-2.288 1.595-3.85 4.62-3.85h3.003v11.868H13.32V7.666z" fill="#fff" />
      </svg>
    )
  },
  {
    id: 'mailru',
    title: 'Mail.ru',
    icon: (
      <svg width="19" height="19" viewBox="0 0 30 30" fill="none" aria-hidden="true">
        <path d="M19.5122 15C19.5122 17.4881 17.4881 19.5122 15 19.5122C12.5119 19.5122 10.4878 17.4881 10.4878 15C10.4878 12.5119 12.5119 10.4878 15 10.4878C17.4881 10.4878 19.5122 12.5119 19.5122 15ZM15 0C6.72938 0 0 6.72938 0 15C0 23.2706 6.72938 30 15 30C18.03 30 20.9522 29.0981 23.4497 27.3909L23.4928 27.3609L21.4716 25.0116L21.4378 25.0341C19.515 26.2706 17.2884 26.925 15 26.925C8.42438 26.925 3.075 21.5756 3.075 15C3.075 8.42438 8.42438 3.075 15 3.075C21.5756 3.075 26.925 8.42438 26.925 15C26.925 15.8522 26.8303 16.7147 26.6438 17.5631C26.2669 19.1109 25.1831 19.5853 24.3694 19.5225C23.5509 19.4559 22.5938 18.8728 22.5872 17.4459V16.3584V15C22.5872 10.8159 19.1841 7.41281 15 7.41281C10.8159 7.41281 7.41281 10.8159 7.41281 15C7.41281 19.1841 10.8159 22.5872 15 22.5872C17.0325 22.5872 18.9384 21.7922 20.3756 20.3475C21.2109 21.6478 22.5731 22.4634 24.1228 22.5872C24.2559 22.5984 24.3919 22.6041 24.5269 22.6041C25.6172 22.6041 26.6991 22.2394 27.5709 21.5766C28.47 20.8941 29.1413 19.9069 29.5125 18.7209C29.5716 18.5297 29.6803 18.0909 29.6813 18.0881L29.6841 18.0722C29.9025 17.1206 30 16.1719 30 15C30 6.72938 23.2706 0 15 0" fill="#0077FF" />
      </svg>
    )
  }
];

class Login extends Component {
  tr = s => t(this.props.lang, s);
  static propTypes = {
    history: PropTypes.objectOf(PropTypes.any),
    location: PropTypes.objectOf(PropTypes.any),
    user: PropTypes.shape({
      email: PropTypes.string
    }),
    login: PropTypes.func,
    loginGoogle: PropTypes.func,
    register: PropTypes.func,
    forgot: PropTypes.func,
    feedback: PropTypes.func,
    getSurvey: PropTypes.func
  };
  turnstileRef = React.createRef();
  googleLoginRef = React.createRef();
  googleRegisterRef = React.createRef();
  renderTimeoutId = null;
  googleScriptTimeoutId = null;
  state = {
    authType: 'login',
    showPassword: false,
    loading: false,
    loginSuccess: false,
    loginNotActivated: false,
    loginError: false,
    loginErrorMsg: '',
    forgotSuccess: false,
    forgotError: false,
    registerSuccess: false,
    registerError: false,
    registerErrorMsg: '',
    registerMsg: '',
    turnstileReady: false,
    turnstileFailed: false,
    turnstileWidgetId: null
  };
  // Cloudflare's challenge script is a third-party load that ad blockers,
  // corporate proxies and some networks (notably CN — zh is a supported
  // locale here) drop outright. Billing's requireTurnstile rejects a missing
  // token, so we can't just submit without one; what we can do is stop
  // polling forever and tell the user, instead of leaving «Зарегистрироваться»
  // disabled with no explanation.
  static TURNSTILE_LOAD_DEADLINE_MS = 8000;
  turnstileAttempts = 0;
  onLogin = async values => {
    if (this.state.authType === 'forgot') {
      await this.onForgot(values);
      return;
    }
    this.setState({ loading: true, loginSuccess: false, loginError: false, loginNotActivated: false, loginErrorMsg: '' });
    // A registrant who abandoned at the profile gate leaves the flag behind;
    // any manual login is a new session, not a registration continuation.
    try { window.localStorage.removeItem(POST_REGISTER_KEY); } catch (e) { /* private mode */ }
    try {
      const res = await this.props.login(values);
      if (shouldRedirectToNewCabinet(this.props.location.search) && res && res.menuJwt) {
        window.location.assign(newCabinetUrl(res.menuJwt, '/'));
        return res;
      }
      this.setState({ loading: false, loginSuccess: true, loginNotActivated: false, loginError: false, forgotSuccess: false, forgotError: false });
      return res;
    } catch (error) {
      const msg = error.message || '';
      const notActivated = msg === 'Your account is not activated!';
      this.setState({
        loading: false,
        loginSuccess: false,
        loginNotActivated: notActivated,
        loginError: !notActivated,
        loginErrorMsg: notActivated ? '' : this.tr(msg),
        forgotSuccess: false,
        forgotError: false
      });
    }
  };
  onRegister = async values => {
    this.setState({ loading: true, registerSuccess: false, registerError: false, registerErrorMsg: '', registerMsg: '' });
    const turnstileToken = window.turnstile && this.state.turnstileWidgetId !== null
      ? window.turnstile.getResponse(this.state.turnstileWidgetId) || ''
      : '';
    try {
      const res = await this.props.register({ ...values, turnstileToken });
      this.props.feedback({ id: 'app:register', email: values.username });
      try {
        // Billing auto-activates the account during /register, so the fresh
        // credentials log in immediately — no email click needed. The profile
        // gate will insert /profile in front of the constructor; the flag
        // routes the user onward after they save it.
        try { window.localStorage.setItem(POST_REGISTER_KEY, '1'); } catch (e) { /* private mode */ }
        await this.props.login({ username: values.username, password: values.password });
        this.props.history.replace('/constructor/sphere');
        return res;
      } catch (autoLoginError) {
        try { window.localStorage.removeItem(POST_REGISTER_KEY); } catch (e) { /* ignore */ }
      }
      this.setState({ loading: false, registerSuccess: true, registerError: false, registerErrorMsg: '', registerMsg: res.msg || '' });
      return res;
    } catch (error) {
      this.setState({
        loading: false,
        registerSuccess: false,
        registerError: true,
        registerErrorMsg: error.message ? this.tr(error.message) : this.tr('Произошла ошибка, попробуйте позже')
      });
      if(window.turnstile && this.state.turnstileWidgetId !== null) {
        window.turnstile.reset(this.state.turnstileWidgetId);
        this.setState({ turnstileReady: false });
      }
    }
  };
  onForgot = async values => {
    try {
      const res = await this.props.forgot(values);
      this.setState({ forgotSuccess: true, forgotError: false, loginSuccess: false, loginNotActivated: false, loginError: false });
      return res;
    } catch (error) {
      this.setState({ forgotSuccess: false, forgotError: true, loginSuccess: false, loginNotActivated: false, loginError: false });
    }
  };
  InputComponent = ({ input, label, meta: { touched, error }, ...rest }) => (
    <div className={styles.row}>
      <input {...input} {...rest} className={touched && error ? styles.labelError : ''} placeholder={label} autoComplete={(input.type === 'email').toString()} />
      {touched && error && <p className={styles.fieldError}>{this.tr(error)}</p>}
    </div>
  );
  componentDidMount() {
    if (this.props.location.search === '?subId=register') this.setState({ authType: 'register' });
    this.showOAuthError();
    this.renderTurnstile();
    this.renderGoogleButton();
  }
  // The redirect providers can only report failure through the URL the cabinet
  // callback bounces to; the message itself is rendered here so it follows the
  // operator's locale.
  showOAuthError = () => {
    const failed = new URLSearchParams(this.props.location.search).get('oauthError');
    const provider = OAUTH_PROVIDERS.find(p => p.id === failed);
    if (!provider) return;
    this.setState({
      loginError: true,
      loginErrorMsg: this.tr(`Не удалось войти через ${provider.title}`)
    });
    this.props.history.replace('/login');
  };
  componentWillUnmount() {
    if(this.renderTimeoutId !== null) {
      clearTimeout(this.renderTimeoutId);
      this.renderTimeoutId = null;
    }
    if(this.googleScriptTimeoutId !== null) {
      clearTimeout(this.googleScriptTimeoutId);
      this.googleScriptTimeoutId = null;
    }
    if(this.state.turnstileWidgetId !== null && window.turnstile) {
      window.turnstile.remove(this.state.turnstileWidgetId);
    }
  }
  renderGoogleButton = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;
    if (!window.google || !window.google.accounts || !window.google.accounts.id) {
      this.googleScriptTimeoutId = setTimeout(this.renderGoogleButton, 100);
      return;
    }
    this.googleScriptTimeoutId = null;
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: this.handleGoogleCredential,
      ux_mode: 'popup'
    });
    // Pill shape so the GIS iframe button matches the Yandex/Mail.ru pills.
    const btnOptions = { theme: 'outline', size: 'large', width: 280, text: 'continue_with', shape: 'pill' };
    if (this.googleLoginRef.current) {
      window.google.accounts.id.renderButton(this.googleLoginRef.current, btnOptions);
    }
    if (this.googleRegisterRef.current) {
      window.google.accounts.id.renderButton(this.googleRegisterRef.current, btnOptions);
    }
  };
  renderOAuthLinks = () => {
    const enabled = {
      yandex: process.env.REACT_APP_YANDEX_ENABLED === '1',
      mailru: process.env.REACT_APP_MAILRU_ENABLED === '1'
    };
    const providers = OAUTH_PROVIDERS.filter(p => enabled[p.id]);
    if (!providers.length) return null;
    return (
      <div className={styles.oauthRow}>
        {providers.map(p => (
          <a key={p.id} href={`/api/v1/login/${p.id}`} className={styles.oauthLink}>
            {p.icon}
            <span className={styles.oauthLabel}>{p.title}</span>
          </a>
        ))}
      </div>
    );
  };
  handleGoogleCredential = async ({ credential }) => {
    if (!credential) return;
    // A registrant who abandoned at the profile gate leaves the flag behind;
    // any manual login is a new session, not a registration continuation.
    try { window.localStorage.removeItem(POST_REGISTER_KEY); } catch (e) { /* private mode */ }
    this.setState({ loading: true, loginError: false, loginErrorMsg: '', loginNotActivated: false, loginSuccess: false });
    try {
      const res = await this.props.loginGoogle(credential);
      if (shouldRedirectToNewCabinet(this.props.location.search) && res && res.menuJwt) {
        window.location.assign(newCabinetUrl(res.menuJwt, '/'));
        return;
      }
      this.setState({ loading: false, loginSuccess: true });
      // One Google button serves both sign-up and sign-in, and neither the
      // token nor the login response says which one happened — so route on
      // the same predicate the /survey chooser and the cabinet hero use:
      // an account with nothing to show gets the constructor, everyone else
      // gets the cabinet (the auth wrapper's default). The flag carries that
      // decision across the profile gate, exactly as after email register.
      const surveys = await this.props.getSurvey('all').catch(() => null);
      if (surveysLoaded(surveys) && !hasVisibleSurveys(surveys)) {
        try { window.localStorage.setItem(POST_REGISTER_KEY, '1'); } catch (e) { /* private mode */ }
        this.props.history.replace('/constructor/sphere');
      }
    } catch (error) {
      this.setState({
        loading: false,
        loginError: true,
        loginErrorMsg: error.message ? this.tr(error.message) : this.tr('Не удалось войти через Google')
      });
    }
  };
  renderTurnstile = () => {
    if(!window.turnstile) {
      this.turnstileAttempts += 1;
      if(this.turnstileAttempts * 100 >= Login.TURNSTILE_LOAD_DEADLINE_MS) {
        this.renderTimeoutId = null;
        this.setState({ turnstileFailed: true });
        return;
      }
      this.renderTimeoutId = setTimeout(this.renderTurnstile, 100);
      return;
    }
    this.renderTimeoutId = null;
    if(!this.turnstileRef.current) return;
    const widgetId = window.turnstile.render(this.turnstileRef.current, {
      sitekey: TURNSTILE_SITEKEY,
      callback: () => this.setState({ turnstileReady: true, turnstileFailed: false }),
      'error-callback': () => this.setState({ turnstileReady: false, turnstileFailed: true }),
      'expired-callback': () => this.setState({ turnstileReady: false })
    });
    this.setState({ turnstileWidgetId: widgetId, turnstileFailed: false });
  };
  retryTurnstile = () => {
    this.turnstileAttempts = 0;
    this.setState({ turnstileReady: false, turnstileFailed: false });
    if(window.turnstile && this.state.turnstileWidgetId !== null) {
      window.turnstile.reset(this.state.turnstileWidgetId);
      return;
    }
    this.renderTurnstile();
  };
  render() {
    const logo = require('../../../static/images/logo/superqr-logo.svg');
    const logo2 = require('../../../static/images/logo/superqr-logo-white.svg');
    // Белый логотип нужен в двух несвязанных случаях: когда он лежит на
    // фирменной панели (регистрация) и когда включена тёмная тема. Первое
    // знает только JS, второе — только CSS, поэтому условия разные.
    const onBrandSide = !(this.state.authType === 'login' || this.state.authType === 'forgot');
    return [
      <div key="1" className={styles.content}>
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 1000 }}>
          <LanguagePicker lang={this.props.lang} langs={LANGS} onSelect={this.props.setLang} />
        </div>
        <div className={styles.inner}>
          <div className={styles.wrap}>
            <div className={styles.authForm}>
              <div className={`${styles.leftSide} ${this.state.authType === 'login' ? styles.mobileWidth : this.state.authType === 'forgot' ? styles.mobileWidth : styles.mobileHide}`}>
                <a className={styles.logo} onClick={() => window.location.href = 'https://superqr.com'}>
                  {onBrandSide
                    ? <img src={logo2} width="140" height="30" alt="SuperQR" />
                    : [
                      <img key="d" className={styles.logoDark} src={logo} width="140" height="30" alt="SuperQR" />,
                      <img key="l" className={styles.logoLight} src={logo2} width="140" height="30" alt="SuperQR" />
                    ]}
                </a>
                <Form
                  onSubmit={values => this.onLogin(values)}
                  validate={this.state.authType !== 'forgot' ? loginValidation : forgotValidation}
                  render={({ handleSubmit }) => (
                    <form method="post" onSubmit={handleSubmit} className={styles.form}>
                      <div className={styles.row} key="1">
                        <h2>{this.state.authType === 'login' || this.state.authType === 'register' ? this.tr('Войти') : this.tr('Сбросить пароль')}</h2>
                      </div>
                      <div className={styles.row} key="2">
                        <p className={styles.shortText}>{this.tr('используйте свой аккаунт')}</p>
                      </div>
                      <div className={styles.row} key="3">
                        <Field type="email" name="username" component={this.InputComponent} label={this.tr('Email')} />
                      </div>
                      {this.state.authType === 'login' || this.state.authType === 'register'
                        ? (
                          <div className={styles.row} key="4">
                            <Field type={this.state.showPassword ? 'text' : 'password'} name="password" component={this.InputComponent} label={this.tr('Пароль')} />
                            <span className={`${styles.eye} ${this.state.showPassword ? styles.showPassword : ''}`} onClick={() => this.setState({ showPassword: !this.state.showPassword })} />
                          </div>
                        )
                        : null}
                      {this.state.loginError
                        && (
                          <div className={styles.row} key="5">
                            <p className={styles.textError}>{this.state.loginErrorMsg || this.tr('Неверный логин или пароль')}</p>
                          </div>
                        )}
                      {this.state.loginNotActivated
                        && (
                          <div className={styles.row} key="6">
                            <p className={styles.textError}>{this.tr('Подтвердите email. Проверьте почту или папку «Спам».')}</p>
                          </div>
                        )}
                      {this.state.loginSuccess
                        && (
                          <div className={styles.row} key="7">
                            <p className={styles.textSuccess}>{this.tr('Успешная авторизация')}</p>
                          </div>
                        )}
                      {this.state.forgotSuccess
                        && (
                          <div className={styles.row} key="8">
                            <p className={styles.textSuccess}>{this.tr('Если аккаунт существует, на почту отправлена ссылка для восстановления пароля')}</p>
                          </div>
                        )}
                      {this.state.forgotError
                        && (
                          <div className={styles.row} key="9">
                            <p className={styles.textError}>{this.tr('Не удалось отправить ссылку. Попробуйте позже.')}</p>
                          </div>
                        )}
                      <div className={styles.row} key="10" style={{ margin: '20px 0' }}>
                        {this.state.authType === 'login' || this.state.authType === 'register'
                          ? <span className={styles.forgotLink} onClick={() => this.setState({ authType: 'forgot' })}>{this.tr('Забыли свой пароль?')}</span>
                          : <span className={styles.forgotLink} onClick={() => this.setState({ authType: 'login' })}>{this.tr('Войти')}</span>}
                      </div>
                      <div className={styles.row} key="11">
                        <button type="submit" className={styles.button} disabled={this.state.loading}>{this.state.loading ? this.tr('Загрузка...') : (this.state.authType === 'login' || this.state.authType === 'register' ? this.tr('Войти') : this.tr('Сбросить'))}</button>
                      </div>
                      <div className={styles.row} key="12" style={{ margin: '16px 0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <span className={styles.authSocialTitle}>{this.tr('или войдите через')}</span>
                        <div ref={this.googleLoginRef} />
                        {this.renderOAuthLinks()}
                        {newCabinetEnabled() && !preferClassic() && <a href="/login?classic=1" className={styles.forgotLink}>{this.tr('Остаться в классическом')}</a>}
                      </div>
                      <div className={`${styles.row} ${styles.mobileVersion}`}>
                        <span className={styles.authSocialTitle}>{this.tr('Еще не зарегистрированы?')}<span className={styles.forgotLink} onClick={() => this.setState({ authType: 'register' })}>{this.tr('Зарегистрироваться')}</span>
                        </span>
                      </div>
                    </form>
                  )}
                />
                <div className={`${styles.mask} ${this.state.authType === 'login' || this.state.authType === 'forgot' ? styles.firstState : ''}`}>
                  <div className={`${styles.maskInner} ${this.state.authType === 'login' || this.state.authType === 'forgot' ? styles.secondInnerState : ''}`}>
                    <h2>{this.tr('С возвращением!')}</h2>
                    <p>{this.tr('У вас уже есть аккаунт?')}</p>
                    <span className={styles.button} onClick={() => this.setState({ authType: 'login' })}>{this.tr('Войти')}</span>
                  </div>
                </div>
              </div>
              <div className={`${styles.rightSide} ${this.state.authType === 'register' ? styles.mobileWidth : styles.mobileHide}`}>
                <Form
                  onSubmit={values => this.onRegister(values)}
                  validate={registerValidation}
                  render={({ handleSubmit }) => (
                    <form method="post" onSubmit={handleSubmit} className={styles.form}>
                      <div className={styles.row} key="1">
                        <h2>{this.tr('Регистрация')}</h2>
                      </div>
                      <div className={styles.row} key="2">
                        <Field type="email" name="username" component={this.InputComponent} label={this.tr('Email')} />
                      </div>
                      <div className={styles.row} key="4">
                        <Field type={this.state.showPassword ? 'text' : 'password'} name="password" component={this.InputComponent} label={this.tr('Пароль')} />
                        <span className={`${styles.eye} ${this.state.showPassword ? styles.showPassword : ''}`} onClick={() => this.setState({ showPassword: !this.state.showPassword })} />
                      </div>
                      <div className={styles.row} key="5" style={{ marginBottom: '20px' }}>
                        <Field type={this.state.showPassword ? 'text' : 'password'} name="passwordConfirm" component={this.InputComponent} label={this.tr('Подтвердите пароль')} />
                        <span className={`${styles.eye} ${this.state.showPassword ? styles.showPassword : ''}`} onClick={() => this.setState({ showPassword: !this.state.showPassword })} />
                      </div>
                      {this.state.registerError
                        && (
                          <div className={styles.row} key="6">
                            <p className={styles.textError}>{this.state.registerErrorMsg}</p>
                          </div>
                        )}
                      {this.state.registerSuccess
                        && (
                          <div className={styles.row} key="8">
                            <p className={styles.textSuccess}>{this.state.registerMsg || this.tr('Регистрация прошла успешно!')}</p>
                          </div>
                        )}
                      <div className={styles.row} key="turnstile">
                        <div ref={this.turnstileRef} />
                      </div>
                      {this.state.turnstileFailed
                        && (
                          <div className={styles.row} key="turnstile-failed">
                            <p className={styles.textError}>{this.tr('Не удалось загрузить проверку безопасности. Отключите блокировщик рекламы или VPN и попробуйте снова — либо зарегистрируйтесь через Google.')}</p>
                            <span className={styles.forgotLink} onClick={this.retryTurnstile}>{this.tr('Повторить проверку')}</span>
                          </div>
                        )}
                      <div className={styles.row} key="9">
                        <button type="submit" className={styles.button} disabled={this.state.loading || !this.state.turnstileReady}>{this.state.loading ? this.tr('Загрузка...') : this.tr('Зарегистрироваться')}</button>
                      </div>
                      <div className={styles.row} key="10" style={{ margin: '16px 0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <span className={styles.authSocialTitle}>{this.tr('или зарегистрируйтесь через')}</span>
                        <div ref={this.googleRegisterRef} />
                        {this.renderOAuthLinks()}
                      </div>
                      <div className={`${styles.row} ${styles.mobileVersion}`} key="11">
                        <span className={styles.authSocialTitle}>{this.tr('Если уже имеете аккаунт войдите в систему:')}<br />
                          <span className={styles.forgotLink} onClick={() => this.setState({ authType: 'login' })}>{this.tr('Войти')}</span>
                        </span>
                      </div>
                    </form>
                  )}
                />
                <div className={`${styles.mask} ${this.state.authType === 'register' ? styles.secondState : ''}`}>
                  <div className={`${styles.maskInner} ${this.state.authType === 'login' || this.state.authType === 'forgot' ? '' : styles.firstInnerState}`}>
                    <h2>{this.tr('Добро пожаловать!')}</h2>
                    <p>{this.tr('Еще не зарегистрированы?')}</p>
                    <span className={styles.button} onClick={() => this.setState({ authType: 'register' })}>{this.tr('Зарегистрироваться')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ];
  }
}

export default connect(
  state => ({
    lang: state.locale.lang,
    user: state.auth.user
  }),
  { ...authActions, setLang, getSurvey }
)(Login);
